import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {deleteCourse, getCourses, updateCourse} from "../api/api";
import {Course} from "../types";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    CircularProgress,
    Grid2,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import {alpha, useTheme} from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";
import {Link} from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import {useAlert} from "../context/AlertContext";
import AppModalBox from "./AppModalBox";
import CourseLogoSelect from "./CourseLogoSelect";
import {themeLogoSvg} from "../helper/logo";

interface Props {
    onAdd: () => void;
    refreshTrigger: boolean;
    refreshCourses: () => void;
}

type CourseWithLogo = Course & {
    logo?: string | null;
};

const pageSize = 12;

const createSvgDataUrl = (svg: string) =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const CourseList = ({
                        onAdd,
                        refreshTrigger,
                        refreshCourses,
                    }: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const {showAlert} = useAlert();

    const [courses, setCourses] = useState<CourseWithLogo[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTitle, setSearchTitle] = useState("");
    const [searchInput, setSearchInput] = useState("");

    const [editingCourse, setEditingCourse] =
        useState<CourseWithLogo | null>(null);

    const [courseToDelete, setCourseToDelete] =
        useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setHasError(false);

        const fetchCourses = async () => {
            try {
                const {
                    courses,
                    totalPages,
                } = await getCourses(page - 1, pageSize, searchTitle);

                if (cancelled) return;

                setCourses(courses);
                setTotalPages(totalPages || 1);
            } catch (error: any) {
                if (cancelled) return;

                setHasError(true);
                setCourses([]);

                showAlert(
                    error?.message ?? t("courseslist.errors.loadFailed"),
                    "error"
                );
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchCourses();

        return () => {
            cancelled = true;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, refreshTrigger, searchTitle]);

    const onSearch = () => {
        setPage(1);
        setSearchTitle(searchInput.trim());
    };

    const handleEditCourse = async (
        event: React.FormEvent
    ) => {
        event.preventDefault();

        if (!editingCourse) return;

        try {
            await updateCourse(
                editingCourse.id.toString(),
                {
                    title: editingCourse.title,
                    description: editingCourse.description,
                    logoId: editingCourse.logoId ?? null,
                }
            );

            showAlert(
                t("courseslist.alerts.updated"),
                "success"
            );

            setEditingCourse(null);
            refreshCourses();
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courseslist.errors.updateFailed"),
                "error"
            );
        }
    };

    const handleDeleteCourse = async () => {
        if (!courseToDelete) return;

        try {
            await deleteCourse(courseToDelete);

            showAlert(
                t("courseslist.alerts.deleted"),
                "success"
            );

            setCourseToDelete(null);
            refreshCourses();
        } catch (error: any) {
            showAlert(
                error?.message ||
                t("courseslist.errors.deleteFailed"),
                "error"
            );
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mb: 3,
                }}
            >
                <IconButton
                    onClick={onAdd}
                    aria-label={t("courseslist.addCourse")}
                    sx={(theme) => ({
                        width: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor:
                        theme.palette.action.hover,
                        color: theme.palette.text.primary,

                        "&:hover": {
                            backgroundColor:
                            theme.palette.action.selected,
                        },
                    })}
                >
                    <AddIcon/>
                </IconButton>

                <Box sx={{flexGrow: 1}}/>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: {xs: "100%", sm: "auto"},
                    }}
                >
                    <TextField
                        type="search"
                        size="small"
                        placeholder={t("courseslist.searchByTitle")}
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            if (!e.target.value && searchTitle) {
                                setPage(1);
                                setSearchTitle("");
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                onSearch();
                            }
                        }}
                        sx={{
                            width: {xs: "100%", sm: 320},
                            flexGrow: {xs: 1, sm: 0},
                        }}
                    />

                    <IconButton
                        onClick={onSearch}
                        disabled={loading}
                        aria-label={t("common.search")}
                        sx={(theme) => ({
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            backgroundColor: theme.palette.action.hover,
                            color: theme.palette.text.primary,
                            flexShrink: 0,
                            "&:hover": {
                                backgroundColor: theme.palette.action.selected,
                            },
                        })}
                    >
                        <SearchIcon fontSize="small"/>
                    </IconButton>
                </Box>
            </Box>

            {loading && courses.length === 0 ? (
                <Box
                    sx={{
                        mt: 6,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <CircularProgress/>
                </Box>
            ) : hasError ? (
                <Box sx={{mt: 4}}>
                    <Button
                        variant="outlined"
                        onClick={refreshCourses}
                    >
                        {t("common.retry")}
                    </Button>
                </Box>
            ) : courses.length === 0 ? (
                <Box
                    sx={(theme) => ({
                        py: 6,
                        px: 3,
                        textAlign: "center",
                        borderRadius: 3,
                        border: `1px dashed ${theme.palette.divider}`,
                        color: "text.secondary",
                    })}
                >
                    <Typography variant="body1">
                        {searchTitle
                            ? t("courseslist.noResults", {title: searchTitle})
                            : t("courseslist.noCourses")}
                    </Typography>
                </Box>
            ) : (
                <Grid2
                    container
                    spacing={3}
                    alignItems="stretch"
                >
                    {courses.map((course) => (
                        <Grid2
                            key={course.id}
                            size={{
                                xs: 12,
                                sm: 6,
                                md: 4,
                            }}
                            sx={{
                                display: "flex",
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={(theme) => ({
                                    width: "100%",
                                    minHeight: 270,
                                    display: "flex",
                                    flexDirection: "column",
                                    position: "relative",
                                    isolation: "isolate",
                                    overflow: "hidden",

                                    border:
                                        `1px solid ${theme.palette.divider}`,

                                    borderRadius: 5,

                                    background:
                                        theme.palette.mode === "dark"
                                            ? `linear-gradient(
                                                135deg,
                                                ${alpha(
                                                theme.palette.primary
                                                    .main,
                                                0.025
                                            )},
                                                ${theme.palette.background.paper}
                                                58%
                                            )`
                                            : `linear-gradient(
                                                135deg,
                                                ${alpha(
                                                theme.palette.primary.main,
                                                0.035
                                            )},
                                                ${theme.palette.background.paper}
                                                58%
                                            )`,

                                    boxShadow:
                                        theme.palette.mode === "dark"
                                            ? "0 12px 30px rgba(0,0,0,0.22)"
                                            : "0 12px 30px rgba(15,23,42,0.06)",

                                    transition:
                                        "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",

                                    "&:hover": {
                                        transform: "translateY(-5px)",

                                        borderColor: alpha(
                                            theme.palette.primary.main,
                                            0.42
                                        ),

                                        boxShadow:
                                            theme.palette.mode === "dark"
                                                ? "0 20px 42px rgba(0,0,0,0.38)"
                                                : "0 20px 42px rgba(15,23,42,0.11)",

                                        "& .admin-course-logo": {
                                            opacity:
                                                theme.palette.mode === "dark"
                                                    ? 0.18
                                                    : 0.13,

                                            transform:
                                                "translateY(-50%) scale(1.08)",
                                        },

                                        "& .admin-course-title": {
                                            color:
                                                theme.palette.primary.main,
                                        },
                                    },
                                })}
                            >
                                {course.logo && (
                                    <Box
                                        component="img"
                                        className="admin-course-logo"
                                        src={createSvgDataUrl(
                                            themeLogoSvg(course.logo, theme)
                                        )}
                                        alt=""
                                        aria-hidden="true"
                                        sx={(theme) => ({
                                            position: "absolute",
                                            zIndex: -1,

                                            width: {
                                                xs: 190,
                                                sm: 205,
                                            },

                                            height: {
                                                xs: 190,
                                                sm: 205,
                                            },

                                            right: {
                                                xs: -38,
                                                sm: -42,
                                            },

                                            top: "45%",
                                            objectFit: "contain",

                                            opacity:
                                                theme.palette.mode === "dark"
                                                    ? 0.12
                                                    : 0.065,

                                            transform:
                                                "translateY(-50%) scale(1)",

                                            transformOrigin: "center",
                                            pointerEvents: "none",
                                            userSelect: "none",

                                            transition:
                                                "opacity 180ms ease, transform 180ms ease",
                                        })}
                                    />
                                )}

                                <Box
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        px: 2.75,
                                        pt: 2.6,
                                        pb: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                        minHeight: 0,
                                    }}
                                >
                                    <Typography
                                        variant="overline"
                                        sx={(theme) => ({
                                            mb: 0.75,
                                            width: "fit-content",
                                            fontWeight: 900,
                                            fontSize: 11,
                                            lineHeight: 1.4,
                                            letterSpacing: "0.12em",

                                            color:
                                                theme.palette.mode === "dark"
                                                    ? alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.65
                                                    )
                                                    : alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.78
                                                    ),
                                        })}
                                    >
                                        {t("courseslist.courseTitle")}
                                    </Typography>

                                    <Typography
                                        className="admin-course-title"
                                        sx={{
                                            maxWidth: course.logo
                                                ? {
                                                    xs: "75%",
                                                    sm: "72%",
                                                }
                                                : "100%",

                                            minHeight: 48,
                                            fontWeight: 900,
                                            fontSize: 20,
                                            lineHeight: 1.25,
                                            letterSpacing: "-0.02em",

                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",

                                            textOverflow: "ellipsis",
                                            wordBreak: "break-word",
                                            overflowWrap: "anywhere",

                                            transition:
                                                "color 180ms ease",
                                        }}
                                    >
                                        {course.title}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1.5,

                                            maxWidth: course.logo
                                                ? {
                                                    xs: "78%",
                                                    sm: "74%",
                                                }
                                                : "100%",

                                            color: "text.secondary",
                                            lineHeight: 1.7,

                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",

                                            textOverflow: "ellipsis",
                                            wordBreak: "break-word",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {course.description}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={(theme) => ({
                                        position: "relative",
                                        zIndex: 2,
                                        px: 1.5,
                                        py: 1.1,
                                        mt: "auto",
                                        flexShrink: 0,

                                        borderTop:
                                            `1px solid ${theme.palette.divider}`,

                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,

                                        backdropFilter: "blur(5px)",

                                        backgroundColor:
                                            theme.palette.mode === "dark"
                                                ? alpha(
                                                    theme.palette.background
                                                        .paper,
                                                    0.78
                                                )
                                                : alpha(
                                                    theme.palette.background
                                                        .paper,
                                                    0.82
                                                ),
                                    })}
                                >
                                    <Button
                                        component={Link}
                                        to={`/course/${course.id}`}
                                        size="small"
                                        sx={{
                                            minWidth: 0,
                                            fontWeight: 700,
                                            color: "text.secondary",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 1.8,
                                            px: 1.2,
                                            py: 0.65,
                                        }}
                                    >
                                        {t("common.open")}
                                    </Button>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.8,
                                        }}
                                    >
                                        <IconButton
                                            component={Link}
                                            to={`/course-access/${course.id}`}
                                            size="small"
                                            aria-label={t(
                                                "courseslist.accessUsers"
                                            )}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1.8,
                                                p: 0.7,
                                            }}
                                        >
                                            <PeopleIcon
                                                sx={{fontSize: 18}}
                                            />
                                        </IconButton>

                                        <IconButton
                                            onClick={() =>
                                                setEditingCourse(course)
                                            }
                                            size="small"
                                            aria-label={t("common.edit")}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1.8,
                                                p: 0.7,
                                            }}
                                        >
                                            <EditIcon
                                                sx={{fontSize: 18}}
                                            />
                                        </IconButton>

                                        <IconButton
                                            onClick={() =>
                                                setCourseToDelete(
                                                    course.id.toString()
                                                )
                                            }
                                            size="small"
                                            aria-label={t("common.delete")}
                                            sx={(theme) => ({
                                                border: "1px solid",
                                                borderColor: "divider",
                                                borderRadius: 1.8,
                                                p: 0.7,

                                                "&:hover": {
                                                    color:
                                                    theme.palette.error.main,

                                                    borderColor: alpha(
                                                        theme.palette.error.main,
                                                        0.45
                                                    ),

                                                    backgroundColor: alpha(
                                                        theme.palette.error.main,
                                                        0.08
                                                    ),
                                                },
                                            })}
                                        >
                                            <DeleteIcon
                                                sx={{fontSize: 18}}
                                            />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid2>
                    ))}
                </Grid2>
            )}

            {totalPages > 1 && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 3,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) =>
                            setPage(value)
                        }
                        color="primary"
                        shape="rounded"
                        disabled={loading}
                    />
                </Box>
            )}

            <DeleteConfirmationModal
                open={courseToDelete !== null}
                onClose={() =>
                    setCourseToDelete(null)
                }
                onConfirm={handleDeleteCourse}
            />

            <Modal
                open={editingCourse !== null}
                onClose={() =>
                    setEditingCourse(null)
                }
            >
                <AppModalBox>
                    <Typography
                        variant="h6"
                        sx={{mb: 2}}
                    >
                        {t("courseslist.editCourse")}
                    </Typography>

                    <form onSubmit={handleEditCourse}>
                        <TextField
                            label={t(
                                "courseslist.courseTitle"
                            )}
                            fullWidth
                            required
                            value={editingCourse?.title || ""}
                            onChange={(event) =>
                                setEditingCourse((previous) =>
                                    previous
                                        ? {
                                            ...previous,
                                            title: event.target.value,
                                        }
                                        : null
                                )
                            }
                            sx={{mb: 2}}
                        />

                        <TextField
                            label={t(
                                "courseslist.courseDescription"
                            )}
                            fullWidth
                            required
                            multiline
                            rows={3}
                            value={
                                editingCourse?.description || ""
                            }
                            onChange={(event) =>
                                setEditingCourse((previous) =>
                                    previous
                                        ? {
                                            ...previous,
                                            description:
                                            event.target.value,
                                        }
                                        : null
                                )
                            }
                            sx={{mb: 2}}
                        />

                        <Box sx={{mb: 2}}>
                            <CourseLogoSelect
                                value={
                                    editingCourse?.logoId ?? null
                                }
                                onChange={(logoId) =>
                                    setEditingCourse((previous) =>
                                        previous
                                            ? {
                                                ...previous,
                                                logoId,
                                            }
                                            : null
                                    )
                                }
                                onError={(message) =>
                                    showAlert(
                                        message,
                                        "error"
                                    )
                                }
                            />
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                        >
                            {t("courseslist.saveChanges")}
                        </Button>
                    </form>
                </AppModalBox>
            </Modal>
        </>
    );
};

export default CourseList;